import { useEffect, useRef, useState } from "react";
import { ImageUp, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { brandUrl, refreshBrand } from "@/lib/brandAssets";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/crm/Badges";
import { Logo } from "@/components/Logo";

const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml,.svg";

const UploadRow = ({ kind, title, hint, info, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("El archivo supera 5 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/brand/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(data);
      toast.success(kind === "logo" ? "Logo actualizado" : "Ícono actualizado");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  const remove = async () => {
    try { onDone((await api.delete(`/admin/brand/${kind}`)).data); toast.success("Eliminado"); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={`brand-${kind}-row`}>
      <p className="text-sm font-semibold text-[#0B2545]">{title}</p>
      <p className="mt-0.5 text-xs text-slate-500">{hint}</p>
      {info && <p className="mt-2 text-xs text-slate-500" data-testid={`brand-${kind}-info`}>{info.original_filename} · {info.width}×{info.height}px · {fmtDateTime(info.uploaded_at)} · {info.uploaded_by}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <input ref={ref} type="file" accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`brand-${kind}-input`} />
        <Button onClick={() => ref.current.click()} disabled={busy} className="rounded-xl bg-[#1E6FD9]" data-testid={`brand-${kind}-upload-button`}>
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ImageUp className="mr-1.5 h-4 w-4" />} {info ? "Reemplazar" : "Subir"}
        </Button>
        {info && <Button variant="outline" onClick={remove} className="rounded-xl" data-testid={`brand-${kind}-remove-button`}><Trash2 className="mr-1.5 h-4 w-4" /> Quitar</Button>}
      </div>
    </div>
  );
};

const Previews = ({ s }) => (
  <div className="space-y-4" data-testid="brand-previews">
    <div className="flex flex-wrap items-center gap-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4"><Logo /></div>
      <div className="rounded-xl bg-[#0B2545] p-4"><Logo light /></div>
    </div>
    <div className="flex flex-wrap items-end gap-4">
      {[16, 32, 180].map((px) => (
        <figure key={px} className="text-center">
          <img src={brandUrl(px === 180 ? s.apple_touch_icon : `/api/public/favicon.png?size=${px}&v=${s.version || 0}`)} alt={`Ícono ${px}px`}
               width={Math.min(px, 64)} height={Math.min(px, 64)} className="rounded border border-slate-200" data-testid={`brand-favicon-${px}`} />
          <figcaption className="mt-1 text-[10px] text-slate-400">{px}px</figcaption>
        </figure>
      ))}
      <figure className="text-center">
        <img src={brandUrl(s.og_image)} alt="Imagen para compartir" width={240} height={126} className="rounded border border-slate-200" data-testid="brand-og-preview" />
        <figcaption className="mt-1 text-[10px] text-slate-400">Al compartir (1200×630)</figcaption>
      </figure>
    </div>
  </div>
);

export const BrandPanel = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/brand").then((r) => setS(r.data)).catch(() => {}); }, []);
  const done = (data) => { setS(data); refreshBrand(); };
  if (!s) return null;
  return (
    <Panel title="Identidad visual" className="mt-6" testid="brand-panel">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <UploadRow kind="logo" title="Logo principal" hint="PNG, JPG, WebP o SVG · máx. 5 MB. Se muestra siempre sobre fondo blanco, sin recortes ni cambios de color."
                     info={s.logo_info} onDone={done} />
          <UploadRow kind="icon" title="Ícono para pestaña (favicon) — opcional" hint="Símbolo cuadrado. Si no lo subes, se genera a partir del logo completo sobre un cuadrado blanco."
                     info={s.icon_info} onDone={done} />
        </div>
        <Previews s={s} />
      </div>
    </Panel>
  );
};
